/* global document, window, IntersectionObserver */

// TODO: This is a performance, lazy-load target. Need to get dynamic imports() working first
require('public/js/polyfill/intersection-observer');
// Styles just for lazy loading (though some adaptations will be necessary in other styles)
require('public/css/elements/lazy-load.scss');

const INTERSECTION_RATIO_MIN = 0;
const LAZY_LOAD_SRC_ATTRIBUTE = 'lazySrc';
const LAZY_LOAD_ELEMENT_CLASS = 'lazy-load';
const LAZY_LOAD_ELEMENT_SELECTOR = `.${LAZY_LOAD_ELEMENT_CLASS}`;
const LAZY_LOAD_ELEMENT_LOADED_CLASS = 'loaded';

let observer;

/**
 * @returns {IntersectionObserverInit}
 */
const getObserverConfig = () => {
	return {
		threshold: 0.01,
		rootMargin: '0px',
	};
};

/**
 * @description Skips waiting on intersection and loads given images
 * @function
 * @param {HTMLImageElement[]} images
 * @returns {void}
 */
const loadImagesNow = (images) => {
	const imagesCount = images.length;
	for (let i = 0; i < imagesCount; i++) {
		const element = images[i];
		addParentClass(element);
		doLazyLoad(element);
	}
};

/**
 * @function
 * @param {unobserve} unobserve
 * @returns {(intersects: IntersectionObserverEntry[]) => void}
 */
const handleIntersection = (unobserve) => {
	return (intersects) => {
		const intersectsCount = intersects.length;
		for (let i=0; i < intersectsCount; i++) {
			const item = intersects[i];
			if (item.intersectionRatio > INTERSECTION_RATIO_MIN) {
				const element = item.target;
				unobserve(element);
				doLazyLoad(element);
			}
		}
	};
};

/**
 * @description Lets load the image!
 * @param {HTMLElement} element
 * @returns {void}
 */
const doLazyLoad = (element) => {
	//If image has already been loaded do not load it again.
	// This prevents duplicate images load when firing the lazyLoad.attach() method multiple times.
	if(element.classList.contains(LAZY_LOAD_ELEMENT_LOADED_CLASS)) {
		return;
	}
	if(element.tagName === 'IMG') {
		loadImage(element);
	} else if (element.getAttribute(LAZY_LOAD_SRC_ATTRIBUTE)) {
		loadBackgroundImage(element);
	} else {
		loadCustom(element);
	}
};

/**
 * @description Remove Lazy Load Classes from arbitrary HTML Element
 * @param {HTMLElement} element
 * @returns {void}
 */
const loadCustom = (element) => {
	element.classList.remove(LAZY_LOAD_ELEMENT_CLASS);
	element.classList.add(LAZY_LOAD_ELEMENT_LOADED_CLASS);
};

/**
 * @description Lazy Load a background Image for an arbitrary HTML Element
 * @param {HTMLElement} element
 * @returns {void}
 */
const loadBackgroundImage = (element) => {
	const src = element.getAttribute(LAZY_LOAD_SRC_ATTRIBUTE);
	element.style.backgroundImage = `url('${src}')`;
	element.classList.remove(LAZY_LOAD_ELEMENT_CLASS);
	element.classList.add(LAZY_LOAD_ELEMENT_LOADED_CLASS);
};

/**
 * @description Lets load the image!
 * @param {HTMLImageElement} element
 * @returns {void}
 */
const loadImage = (element) => {
	const imageParent = element.parentNode;
	const image = element.cloneNode();
	const src = element.getAttribute(LAZY_LOAD_SRC_ATTRIBUTE);

	image.src = src;
	image.alt = element.getAttribute('alt');
	image.classList.add('full-res');
	image.classList.remove('placeholder');

	// Remove the lazySrc attribute
	image.removeAttribute(LAZY_LOAD_SRC_ATTRIBUTE);
	imageParent.insertBefore(image, element);

	// ADA: Update alt of placeholder image so that there are not adjacent duplicates
	const alt = element.getAttribute('alt');
	if (alt) {
		element.setAttribute('alt', `${alt} Placeholder`);
	}

	// Remove placeholder image title
	if (element.getAttribute('title')) {
		element.removeAttribute('title');
	}

	// Lets create a new image, add to DOM, then apply CSS to cross-fade
	image.onload = () => {
		imageParent.classList.add('flip');
	};

	// Add a loaded class to the cloned and original elements
	// We do this just so we can know not to reload the images
	image.classList.add('loaded');
	element.classList.add('loaded');
};

/**
 * @description Sets up for lazy loading
 * @param {HTMLImageElement} element
 * @returns {void}
 */
const addParentClass = (element) => {
	if(element.tagName === 'IMG') {
		const imageParent = element.parentNode;
		imageParent.classList.add('lazy-load-wrap');
	}
};

/**
 * @description Attach IntersectionObserver to given elements
 * @param {HTMLElement[]} imageElements
 * @returns {void}
 */
const attachObserver = (imageElements) => {
	const elements = imageElements || document.querySelectorAll(LAZY_LOAD_ELEMENT_SELECTOR);
	const imageCount = elements.length;
	if (!('IntersectionObserver' in window)) {
		loadImagesNow(elements);
	} else {
		if (!observer) {
			observer = new IntersectionObserver(handleIntersection(unobserve), getObserverConfig());
		}

		for (let i=0; i < imageCount; i++) {
			const item = elements[i];
			// Setup elements for lazy loading
			addParentClass(item);
			// Prevent attaching multiple observers
			unobserve(item);
			// Finally, observe element for viewport intersection
			observe(item);
		}
	}
};

/**
 * @description Call to stop observing a DOM element
 * @function
 * @param {HTMLElement} element
 * @returns {void}
 */
const unobserve = (element) => {
	if (observer) {
		observer.unobserve(element);
	}
};

/**
 * @description Call to start observing a DOM element
 * @param {HTMLElement} element
 * @returns {void}
 */
const observe = (element) => {
	if (observer) {
		observer.observe(element);
	}
};

/**
 * @description Call to stop observing a DOM elements
 * @param {HTMLElement[]} elements
 * @returns {void}
 */
const unobserveAll = (elements) => {
	if (elements && elements.length) {
		for (let i=0; i < elements.length; i++) {
			unobserve(elements[i]);
		}
	} else {
		unobserve(elements);
	}
};

module.exports = () => {
	return Object.freeze({
		getSelector() { return LAZY_LOAD_ELEMENT_SELECTOR; },
		attach: attachObserver,
		detatch: unobserveAll,
	});
};
